export const MENU_LOAD_TIMEOUT_MS = 45000;

export interface MenuLoadStatus {
  templateDetailError: string | null;
  servicesDue: { loaded: boolean; count: number; error: string | null };
  roHistoryError: string | null;
  menuDataError: string | null;
  hasTransactionId: boolean;
  transactionType: string | null;
  vehicleArrived: boolean;
  serviceGroupsArrived: boolean;
  transactionArrived: boolean;
  templateDetailArrived: boolean;
  roHistoryArrived: boolean;
  timedOut: boolean;
}

const MISSING_TRANSACTION_ID = 'XSELLERATOR did not supply a work order or appointment to open this menu for.';
const NO_SERVICES_DUE = "No services were returned for this vehicle's maintenance schedule.";
const VEHICLE_NOT_LOADED = 'The vehicle could not be loaded.';
const SERVICE_GROUPS_NOT_LOADED = 'No service groups were loaded for this rooftop.';
const APPOINTMENT_NOT_LOADED = 'The appointment could not be loaded.';
const WORK_ORDER_NOT_LOADED = 'The work order could not be loaded.';
const TEMPLATE_NOT_LOADED = 'The maintenance schedule template for this vehicle could not be loaded.';
const SERVICES_DUE_NOT_LOADED = 'The services due for this vehicle could not be loaded.';
const RO_HISTORY_NOT_LOADED = 'The service history for this vehicle could not be loaded.';

function describeScheduleProblem(status: MenuLoadStatus): string | null {
  if (status.templateDetailError != null) {
    return status.templateDetailError;
  }
  if (status.servicesDue.error != null) {
    return status.servicesDue.error;
  }
  if (status.servicesDue.loaded && status.servicesDue.count === 0) {
    return NO_SERVICES_DUE;
  }
  if (status.roHistoryError != null) {
    return status.roHistoryError;
  }
  return null;
}

function describeTimedOutLoads(status: MenuLoadStatus): string[] {
  const problems: string[] = [];
  if (!status.vehicleArrived) {
    problems.push(VEHICLE_NOT_LOADED);
  }
  if (!status.serviceGroupsArrived) {
    problems.push(SERVICE_GROUPS_NOT_LOADED);
  }
  if (status.hasTransactionId && !status.transactionArrived) {
    problems.push(status.transactionType === 'appointment' ? APPOINTMENT_NOT_LOADED : WORK_ORDER_NOT_LOADED);
  }
  if (status.templateDetailError == null && !status.templateDetailArrived) {
    problems.push(TEMPLATE_NOT_LOADED);
  }
  if (status.servicesDue.error == null && !status.servicesDue.loaded) {
    problems.push(SERVICES_DUE_NOT_LOADED);
  }
  // The history lookup is only dispatched once services due have loaded with rows, so it can only
  // be the missing piece after that prerequisite is met.
  const historyExpected = status.servicesDue.loaded && status.servicesDue.count > 0 && status.roHistoryError == null;
  if (historyExpected && !status.roHistoryArrived) {
    problems.push(RO_HISTORY_NOT_LOADED);
  }
  return problems;
}

/**
 * Turns the menu's load state into the user-facing reasons it cannot be shown. Empty means the menu
 * is either rendered or still loading inside the timeout. Definitive failures (no schedule, failed
 * calls, missing ids) are reported immediately; loads that simply never arrive are reported once the
 * timeout has elapsed, and clear again if the data turns up later.
 */
export function describeMenuLoadProblems(status: MenuLoadStatus): string[] {
  const problems: string[] = [];
  if (!status.hasTransactionId) {
    problems.push(MISSING_TRANSACTION_ID);
  }
  const scheduleProblem = describeScheduleProblem(status);
  if (scheduleProblem != null) {
    problems.push(scheduleProblem);
  }
  if (status.menuDataError != null) {
    problems.push(status.menuDataError);
  }
  if (status.timedOut) {
    problems.push(...describeTimedOutLoads(status));
  }
  return problems;
}
